import React, { useState, useEffect, useCallback } from 'react';

const C = {
  primary: '#0F6E56', primaryBg: '#E1F5EE', border: '#E3E1D9',
  text: '#1F1E1B', muted: '#5F5E5A', surface: '#F7F5F0',
  gold: '#91600C', danger: '#C0392B',
};

const API = '/api/health';

function authHeaders() {
  const t = localStorage.getItem('token') || '';
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Token ${t}` } : {}) };
}

function when(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const M = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()}-${M[d.getMonth()]}`;
}

const btn = {
  fontSize: 13, fontWeight: 700, padding: '9px 18px', border: 'none',
  borderRadius: 8, background: C.primary, color: '#fff', cursor: 'pointer',
};
const inp = {
  width: '100%', padding: '9px 11px', fontSize: 13.5,
  border: `1px solid ${C.border}`, borderRadius: 7,
  boxSizing: 'border-box', marginBottom: 9,
};

/**
 * Doctor ki kamai - har mukammal checkup ka hissa, aur withdrawal.
 * Sirf doctor ko nazar aata hai; aam user ko saaf paigham milta hai.
 */
export default function DoctorEarnings() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [amount, setAmount] = useState('');
  const [wallet, setWallet] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    fetch(`${API}/doctor/earnings/`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((j) => {
        if (j.error) { setErr(j.error); return; }
        setD(j);
        setWallet(j.pi_wallet_address || '');
      })
      .catch(() => setErr('Could not load your earnings.'));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (err) {
    return (
      <p style={{ textAlign: 'center', color: C.muted, fontSize: 13, padding: '2rem 1rem' }}>
        {err}
      </p>
    );
  }
  if (!d) {
    return <p style={{ textAlign: 'center', color: C.muted, padding: '2rem 0' }}>Loading…</p>;
  }

  const withdraw = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setMsg('Enter an amount.'); return; }
    if (amt > d.balance.available) {
      setMsg(`You only have ${d.balance.available} Pi available.`);
      return;
    }
    if (!wallet.trim()) { setMsg('Add your Pi wallet address first.'); return; }

    if (!window.confirm(
      `Withdraw ${amt} Pi?\n\nTo: ${wallet.slice(0, 24)}…\n\n` +
      'An admin reviews and sends it manually — usually within a day or two.'
    )) return;

    setBusy(true);
    setMsg('');
    try {
      const res = await fetch(`${API}/doctor/withdraw/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ amount: amt, pi_wallet_address: wallet }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Could not request.');
      setMsg('Request sent. You will be notified when it is paid.');
      setAmount('');
      load();
    } catch (e) {
      setMsg(e.message);
    }
    setBusy(false);
  };

  const box = (label, value, color) => (
    <div style={{
      flex: '1 1 100px', background: '#fff', border: `1px solid ${C.border}`,
      borderLeft: `3px solid ${color}`, borderRadius: 9, padding: '9px 11px',
    }}>
      <p style={{ margin: 0, fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </p>
      <p style={{ margin: '3px 0 0', fontSize: 17, fontWeight: 700, color }}>{value} Pi</p>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {box('Available', d.balance.available, C.primary)}
        {box('Earned', d.balance.earned, C.text)}
        {box('Withdrawn', d.balance.withdrawn, C.muted)}
      </div>

      <div style={{
        background: C.surface, borderRadius: 9, padding: '9px 12px', marginBottom: 12,
        display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
      }}>
        <span style={{ fontSize: 12, color: C.muted }}>
          This week <b style={{ color: C.text }}>{d.this_week} Pi</b>
        </span>
        <span style={{ fontSize: 12, color: C.muted }}>
          This month <b style={{ color: C.text }}>{d.this_month} Pi</b>
        </span>
        <span style={{ fontSize: 12, color: C.muted }}>{d.checkups} checkups</span>
      </div>

      {!d.consultation_fee_pi && (
        <div style={{
          background: '#FDF0DD', border: `1px solid ${C.gold}`, borderRadius: 9,
          padding: '10px 12px', marginBottom: 12,
        }}>
          <p style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: C.gold }}>
            {'\u26A0\uFE0F'} Set your consultation fee
          </p>
          <p style={{ margin: '3px 0 0', fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>
            Patients on unlimited plans, and anyone paying per visit, are charged your own fee.
            It is currently 0, so you earn nothing from them. Set it in <b>Become a Doctor</b>.
          </p>
        </div>
      )}

      <div style={{
        background: '#fff', border: `1px solid ${C.border}`, borderRadius: 9,
        padding: 12, marginBottom: 14,
      }}>
        <p style={{ margin: '0 0 3px', fontSize: 13.5, fontWeight: 700 }}>
          {'\u{1F4B0}'} Withdraw your earnings
        </p>
        <p style={{ margin: '0 0 9px', fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
          An admin sends the Pi manually — automatic payouts are not available on Pi Mainnet yet.
        </p>

        <input
          value={wallet}
          onChange={(e) => setWallet(e.target.value)}
          placeholder="Your Pi wallet address"
          style={inp}
        />
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          type="number"
          step="0.000001"
          placeholder={`Amount in Pi (up to ${d.balance.available})`}
          style={inp}
        />

        {msg && (
          <p style={{
            fontSize: 12, margin: '0 0 8px',
            color: msg.startsWith('Request') ? C.primary : C.danger,
          }}>
            {msg}
          </p>
        )}

        <button
          onClick={withdraw}
          disabled={busy || d.balance.available <= 0}
          style={{ ...btn, background: d.balance.available > 0 ? C.primary : '#B4B2A9' }}
        >
          {busy ? 'Sending…' : 'Request withdrawal'}
        </button>
      </div>

      {d.withdrawals.length > 0 && (
        <>
          <p style={{ fontSize: 12.5, fontWeight: 700, margin: '0 0 7px' }}>Your withdrawals</p>
          {d.withdrawals.map((w) => (
            <div key={w.id} style={{
              background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
              padding: '8px 11px', marginBottom: 5, display: 'flex',
              justifyContent: 'space-between', gap: 8, flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{w.amount} Pi</span>
              <span style={{
                fontSize: 11, fontWeight: 700,
                color: w.status === 'paid' ? C.primary : w.status === 'rejected' ? C.danger : C.gold,
              }}>
                {w.status} · {when(w.at)}
              </span>
              {w.note && (
                <p style={{ margin: 0, fontSize: 11, color: C.muted, width: '100%' }}>{w.note}</p>
              )}
            </div>
          ))}
        </>
      )}

      <p style={{ fontSize: 12.5, fontWeight: 700, margin: '14px 0 7px' }}>
        Earnings from checkups
      </p>
      {d.earnings.length === 0 ? (
        <p style={{ fontSize: 12, color: C.muted, textAlign: 'center', padding: '1.5rem 0' }}>
          Nothing yet. You earn when you mark an appointment as complete.
        </p>
      ) : (
        d.earnings.map((e) => (
          <div key={e.id} style={{
            background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
            padding: '8px 11px', marginBottom: 5,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5 }}>@{e.patient} · {e.source}</span>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: C.primary }}>
                +{e.amount_pi} Pi
              </span>
            </div>
            <p style={{ margin: '2px 0 0', fontSize: 10.5, color: C.muted }}>
              {e.gross_pi} Pi − {e.fee_pi} Pi platform fee · {when(e.at)}
            </p>
          </div>
        ))
      )}
    </div>
  );
}
